import { Button, Card } from "@rumbledirect/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message, Typography, Upload, UploadProps } from "antd";
import { useState } from "react";
import { MIME_TYPE_MERGE_PATCH_JSON } from "@rumble-it/common-lib/src/constants/mimeTypes";
import { useViewConfigurationClient } from "../client";

type CustomRequest = NonNullable<UploadProps["customRequest"]>;
type CustomRequestOptions = Parameters<CustomRequest>[0];

export interface UploadCardProps {
  title: string;
  description: string;
  uploadConfig: {
    endpoint: string;
    fileKey: string;
    text: string;
  };
}

const Dragger = Upload;

type UploadVariables = {
  file: CustomRequestOptions["file"];
  onProgress?: CustomRequestOptions["onProgress"];
};

export const UploadCard = ({
  title,
  description,
  uploadConfig,
}: UploadCardProps) => {
  const axiosInstance = useViewConfigurationClient();
  const queryClient = useQueryClient();
  const [fileList, setFileList] = useState<UploadProps["fileList"]>([]);

  const uploadMutation = useMutation({
    mutationFn: async ({ file, onProgress }: UploadVariables) => {
      const formData = new FormData();
      formData.append("file", file);

      const uploadResponse = await axiosInstance.post("/media", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          if (!onProgress || !event.total) return;
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress({ percent }, file);
        },
      });

      await axiosInstance.patch(
        uploadConfig.endpoint,
        { [uploadConfig.fileKey]: uploadResponse.data.id },
        { headers: { "Content-Type": MIME_TYPE_MERGE_PATCH_JSON } },
      );

      return uploadResponse.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["view-configuration"] });
    },
    onError: () => {
      message.error(
        "Er is een fout opgetreden bij het uploaden van het bestand.",
      );
    },
  });

  const handleChange: UploadProps["onChange"] = (info) => {
    const nextList = info.fileList.slice(-1);
    setFileList(nextList);
  };

  const handleCustomRequest: CustomRequest = (options) => {
    const file = options.file;

    uploadMutation.mutate(
      { file, onProgress: options.onProgress },
      {
        onSuccess: (data) => {
          options.onSuccess?.(data, file);
        },
        onError: () => {
          options.onError?.(
            new Error(
              "Er is een fout opgetreden bij het uploaden van het bestand.",
            ),
            file,
          );
        },
      },
    );
  };

  const uploadProps: UploadProps = {
    name: "file",
    multiple: false,
    customRequest: handleCustomRequest,
    fileList,
    onChange: handleChange,
  };

  return (
    <Card title={title}>
      <Typography.Paragraph>{description}</Typography.Paragraph>
      <Dragger {...uploadProps} style={{ width: "100%" }}>
        <Button type="primary" style={{ width: "100%" }}>
          {uploadConfig.text}
        </Button>
      </Dragger>
    </Card>
  );
};
